import { useState, useEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import type { AppState } from "src/store";
import type { WhereFilterOp } from "@firebase/firestore-types";
import "firebase/storage";
import "firebase/firestore";
import firestore from "src/apiService/firestore";

import { getUserId, getUserClaims } from "src/config";
import { setActiveDialog } from "src/store/adminHtml/actions";
import {
  addInventoryRecords,
  updateInventoryRecords,
  deleteInventoryRecords,
} from "src/store/inventory/actions";
import {
  addUploadRecords,
  updateUploadRecords,
  deleteUploadRecords,
  addDownloadRecords,
  updateDownloadRecords,
  deleteDownloadRecords,
  setPendingInventoryExists,
} from "src/store/uploads/actions";
import { setUser, updateUserClaims } from "src/store/system/actions";
import { initialSalesTransactionsSyncCompleteSelector } from "src/store/userDataLoad/selector";
import {
  addErrorAlertRecords,
  deleteErrorAlertRecords,
  updateErrorAlertRecords,
} from "src/store/errorAlerts/actions";
import {
  addPlaidIntegrationRecords,
  deletePlaidIntegrationRecords,
  updatePlaidIntegrationRecords,
} from "src/store/plaidIntegration/actions";
import {
  addGeneralLedgerAccounts,
  deleteGeneralLedgerAccounts,
  updateGeneralLedgerAccounts,
} from "src/store/glAccounts/actions";
import {
  addVendors,
  deleteVendors,
  updateVendors,
} from "src/store/vendor/actions";
import { increaseEmptyTablesCounter } from "src/apiService/modules/users";
import { updateUserApi } from "src/apiService/modules/system";
import { recordInitialLoadTimings } from "src/utils/recordLoadTimings";
import {
  endLoadTiming,
  resetLoadTimings,
  startLoadTiming,
} from "src/utils/loadTimings";
import { getBrowserTimeZone, isValidIanaTimeZone } from "src/utils/timezone";
import {
  addBankAccountsRecords,
  updateBankAccountsRecords,
  deleteBankAccountsRecords,
} from "src/store/bankAccount/actions";

const TimezoneBackfillMaxRetries = 3;
const TimezoneBackfillRetryDelayMS = 1000;

async function wrapListener<T>(
  label: string,
  p: Promise<T>,
  section = "useUserDataLoader",
): Promise<T> {
  const timingName = label;
  const consoleLabel = section ? `[${section}] ${label}` : label;
  startLoadTiming(timingName);
  console.time(consoleLabel);
  try {
    return await p;
  } finally {
    console.timeEnd(consoleLabel);
    endLoadTiming(timingName);
  }
}

function startListener(
  dispatch,
  signal: AbortSignal,
  collectionName: string,
  userId: string,
  addAction,
  updateAction,
  deleteAction,
  extraWhereClauses: Array<[string, WhereFilterOp, any]> = [],
  callback?: (notEmpty: boolean) => void,
) {
  let first = true;
  const db = firestore();
  return new Promise<void>((rs, rj) => {
    let query: any = db.collection(collectionName).where("user", "==", userId);
    for (const [field, op, value] of extraWhereClauses) {
      query = query.where(field, op, value);
    }
    const listener = query.onSnapshot(
      (snapshot) => {
        if (signal.aborted) return;
        console.log(`received ${collectionName} snapshot`);
        const adds: any[] = [];
        const changes: any[] = [];
        const deletes: any[] = [];
        snapshot.docChanges().forEach((change) => {
          const data = change.doc.data();
          if (data && !data.id) data.id = change.doc.id;
          if (change.type === "added") {
            adds.push(data);
          } else if (change.type === "modified") {
            changes.push(data);
          } else if (change.type === "removed") {
            deletes.push(data);
          }
        });
        if (adds.length) dispatch(addAction(adds));
        if (updateAction && changes.length) dispatch(updateAction(changes));
        if (deletes.length) dispatch(deleteAction(deletes));

        if (callback)
          callback(!adds.length && !changes.length && !deletes.length);

        if (first) {
          first = false;
          rs();
        }
      },
      (error) => {
        console.error(
          `Snapshot error when reading from ${collectionName} collection as user ${userId}`,
          error,
        );
        if (first) {
          first = false;
          rj(error);
        }
      },
    );
    signal.addEventListener("abort", () => listener());
  });
}

/**
 * Auth + claims + `Users/{uid}` snapshot gate, then attaches auxiliary collection listeners.
 *
 * @returns Resolves to a promise that settles after each auxiliary listener’s first snapshot
 *   (or listener error). That inner promise is started before return (listeners are live).
 *   Returns `null` if `signal` is aborted after the user phase (auxiliary listeners not started).
 */
async function loadUserDataListeners(
  dispatch,
  signal: AbortSignal,
): Promise<Promise<void> | null> {
  const userId = await wrapListener("getUserId", getUserId());
  const claims = await wrapListener("getUserClaims", getUserClaims());
  dispatch(updateUserClaims({ claims }));
  const db = firestore();

  let tourDisplayed = false;
  let isFirstUserSnapshot = true;
  let timezoneBackfillStarted = false;
  let userSnapshotReady = false;
  await wrapListener(
    "user",
    new Promise<void>((rs, rj) => {
      const wait = (ms: number) =>
        new Promise<void>((resolve) => setTimeout(resolve, ms));
      const markUserSnapshotReady = () => {
        if (userSnapshotReady) return;
        userSnapshotReady = true;
        rs();
      };
      const dispatchUser = (userData) => {
        if (!userData) return;
        dispatch(
          setUser({
            user: { ...userData, id: userData.id ?? userId },
            optimisticDateRanges: !isFirstUserSnapshot,
          }),
        );
        isFirstUserSnapshot = false;
      };
      const startTimezoneBackfill = (receivedUser) => {
        if (timezoneBackfillStarted) return;
        timezoneBackfillStarted = true;
        const tz = getBrowserTimeZone();
        if (!tz) {
          timezoneBackfillStarted = false;
          return;
        }
        void (async () => {
          for (
            let attempt = 1;
            attempt <= TimezoneBackfillMaxRetries;
            attempt++
          ) {
            try {
              await updateUserApi({ timezone: tz });
              return;
            } catch (err) {
              if (attempt === TimezoneBackfillMaxRetries) {
                console.error(
                  "Failed to persist user timezone after retries",
                  err,
                );
                if (!signal.aborted && !userSnapshotReady) {
                  dispatchUser({ ...receivedUser, timezone: tz });
                  markUserSnapshotReady();
                }
                return;
              }
              await wait(TimezoneBackfillRetryDelayMS * attempt);
            }
          }
        })();
      };
      const listener = db
        .collection("Users")
        .doc(userId)
        .onSnapshot(
          (doc: any) => {
            if (signal.aborted) return;

            if (!doc.exists) {
              console.warn(`User document '${userId}' doesn't exist`);
              return;
            }

            const receivedUser = doc.data();
            const storedTimezone =
              typeof receivedUser?.timezone === "string"
                ? receivedUser.timezone.trim()
                : "";
            const hasValidStoredTimezone = isValidIanaTimeZone(storedTimezone);
            if (hasValidStoredTimezone) {
              markUserSnapshotReady();
            }
            if (
              receivedUser &&
              !hasValidStoredTimezone &&
              !timezoneBackfillStarted
            ) {
              startTimezoneBackfill(receivedUser);
            }

            if (receivedUser) {
              if (!receivedUser.acceptedTOS) {
                dispatch(setActiveDialog("accept_terms"));
              } else if (!tourDisplayed && receivedUser.qualifyingQuestions) {
                if (
                  !receivedUser.getStartedStep &&
                  (receivedUser.emptyTablesCount || 0) < 3
                ) {
                  increaseEmptyTablesCounter();
                  dispatch(setActiveDialog("empty_tables"));
                }
              }
            }

            dispatchUser(receivedUser);
          },
          (error) => {
            console.error(
              `Snapshot error when reading user data for user ${userId}.`,
              error,
            );
            rj(error);
          },
        );

      signal.addEventListener("abort", () => listener());
    }),
  );

  if (signal.aborted) return null;

  return Promise.allSettled([
    wrapListener(
      "Inventory",
      startListener(
        dispatch,
        signal,
        "Inventory",
        userId,
        addInventoryRecords,
        updateInventoryRecords,
        deleteInventoryRecords,
      ),
    ),
    wrapListener(
      "Uploads",
      startListener(
        dispatch,
        signal,
        "Uploads",
        userId,
        addUploadRecords,
        updateUploadRecords,
        deleteUploadRecords,
      ),
    ),
    wrapListener(
      "Downloads",
      startListener(
        dispatch,
        signal,
        "Downloads",
        userId,
        addDownloadRecords,
        updateDownloadRecords,
        deleteDownloadRecords,
      ),
    ),
    wrapListener(
      "GL_Accounts",
      startListener(
        dispatch,
        signal,
        "GL_Accounts",
        userId,
        addGeneralLedgerAccounts,
        updateGeneralLedgerAccounts,
        deleteGeneralLedgerAccounts,
      ),
    ),
    wrapListener(
      "Plaid_Integrations",
      startListener(
        dispatch,
        signal,
        "Plaid_Integrations",
        userId,
        addPlaidIntegrationRecords,
        updatePlaidIntegrationRecords,
        deletePlaidIntegrationRecords,
      ),
    ),
    wrapListener(
      "Error_Alerts",
      startListener(
        dispatch,
        signal,
        "Error_Alerts",
        userId,
        addErrorAlertRecords,
        updateErrorAlertRecords,
        deleteErrorAlertRecords,
        [["new", "==", true]],
      ),
    ),
    wrapListener(
      "Vendors",
      startListener(
        dispatch,
        signal,
        "Vendors",
        userId,
        addVendors,
        updateVendors,
        deleteVendors,
      ),
    ),
    wrapListener(
      "Bank_Accounts",
      startListener(
        dispatch,
        signal,
        "Bank_Accounts",
        userId,
        addBankAccountsRecords,
        updateBankAccountsRecords,
        deleteBankAccountsRecords,
      ),
    ),
    // Existence-only listener for pending inventory notification (limit 1).
    wrapListener(
      "Pending_Inventory_exists",
      new Promise<void>((rs, rj) => {
        const db = firestore();
        const listener = db
          .collection("Pending_Inventory")
          .where("user", "==", userId)
          .limit(1)
          .onSnapshot(
            (snapshot) => {
              if (signal.aborted) return;
              dispatch(setPendingInventoryExists(!snapshot.empty));
              rs();
            },
            (error) => {
              console.error(
                "Snapshot error when reading Pending_Inventory existence",
                error,
              );
              rj(error);
            },
          );
        signal.addEventListener("abort", () => listener());
      }),
    ),
  ]).then((results) => {
    results.forEach((r, i) => {
      if (r.status === "rejected") {
        const labels = [
          "Inventory",
          "Uploads",
          "Downloads",
          "GL_Accounts",
          "Plaid_Integrations",
          "Error_Alerts",
          "Vendors",
          "Bank_Accounts",
          "Pending_Inventory_exists",
        ];
        console.error(
          `Auxiliary user-data listener failed (${labels[i]})`,
          r.reason,
        );
      }
    });
  });
}

export type UserDataLoaderStatus = {
  /** Auth, token claims, and `Users/{uid}` snapshot gate (incl. timezone). Enables `DataLoaderManager`. */
  isCoreUserDataReady: boolean;
  /** First snapshot (or terminal error) for Inventory, Uploads, GL, etc. */
  isAuxiliaryUserDataReady: boolean;
};

function useUserDataLoader(): UserDataLoaderStatus {
  const [isCoreUserDataReady, setIsCoreUserDataReady] = useState(false);
  const [isAuxiliaryUserDataReady, setIsAuxiliaryUserDataReady] =
    useState(false);
  const initialSalesTransactionsSyncComplete = useSelector(
    initialSalesTransactionsSyncCompleteSelector,
  );
  const dispatch = useDispatch();
  const store = useStore<AppState>();

  useEffect(() => {
    const abortController = new AbortController();
    let cancelled = false;
    resetLoadTimings();
    (async () => {
      try {
        const auxiliaryFirstSnapshots = await wrapListener(
          "coreUserData",
          loadUserDataListeners(dispatch, abortController.signal),
        );
        if (cancelled || abortController.signal.aborted) return;
        setIsCoreUserDataReady(true);

        if (auxiliaryFirstSnapshots) {
          await wrapListener("auxiliaryCollections", auxiliaryFirstSnapshots);
        }
        if (cancelled || abortController.signal.aborted) return;
        setIsAuxiliaryUserDataReady(true);
      } catch (error) {
        console.trace(error);
      }
    })();

    return () => {
      cancelled = true;
      console.log("useUserDataLoader - abort");
      abortController.abort();
      setIsCoreUserDataReady(false);
      setIsAuxiliaryUserDataReady(false);
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      !isCoreUserDataReady ||
      !isAuxiliaryUserDataReady ||
      !initialSalesTransactionsSyncComplete
    ) {
      return;
    }
    recordInitialLoadTimings(store);
  }, [
    isCoreUserDataReady,
    isAuxiliaryUserDataReady,
    initialSalesTransactionsSyncComplete,
    store,
  ]);

  return { isCoreUserDataReady, isAuxiliaryUserDataReady };
}

export default useUserDataLoader;
